import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  LogOut,
  Menu,
  PlaneTakeoff,
  UserRound,
  X,
} from "lucide-react";
import { supabase } from "../supabaseClient";
import strobeLogo from "../assets/images/strobelogo.svg";
import { clearCachedUserAccess } from "../utils/authRoles";
import { withTimeout } from "../utils/asyncTimeout";
import { traceAsync } from "../utils/perfTrace";
import { formatDisplayName } from "../utils/displayName";

function initialsFrom(user) {
  const meta = user?.user_metadata || {};
  const first = (meta.first_name || meta.firstName || "").trim();
  const last = (meta.last_name || meta.lastName || "").trim();
  if (first || last) return `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase();
  return user?.email ? user.email[0].toUpperCase() : "A";
}

function displayNameFrom(user) {
  const meta = user?.user_metadata || {};
  const first = (meta.first_name || meta.firstName || "").trim();
  const last = (meta.last_name || meta.lastName || "").trim();
  if (first || last) return formatDisplayName(`${first} ${last}`.trim());
  return user?.email || "Admin";
}

function rolePill(adminRole) {
  if (adminRole === "master_admin") return "SUPER ADMIN";
  if (adminRole === "scheduler") return "TEAM MEMBER";
  return "ADMIN";
}

const ADMIN_IDENTITY_CACHE_KEY = "strobe.adminSidebarIdentity";

function readCachedIdentity() {
  try {
    return JSON.parse(sessionStorage.getItem(ADMIN_IDENTITY_CACHE_KEY) || "null");
  } catch {
    return null;
  }
}

function cacheIdentity(identity) {
  if (!identity) {
    sessionStorage.removeItem(ADMIN_IDENTITY_CACHE_KEY);
    return;
  }

  sessionStorage.setItem(ADMIN_IDENTITY_CACHE_KEY, JSON.stringify(identity));
}

export default function AdminSidebar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [adminRole, setAdminRole] = useState("");
  const [cachedIdentity, setCachedIdentity] = useState(() => readCachedIdentity());
  const [isOpen, setIsOpen] = useState(false);

  const loadAdminIdentity = async (nextUser) => {
    if (!nextUser?.id) {
      cacheIdentity(null);
      setCachedIdentity(null);
      setUser(null);
      setAdminRole("");
      return;
    }

    const { data } = await traceAsync(
      "sidebar.admin.role",
      () => supabase
        .from("admin_users")
        .select("role")
        .eq("user_id", nextUser.id)
        .maybeSingle(),
      {}
    );

    const nextRole = data?.role || "";
    const nextIdentity = {
      displayName: displayNameFrom(nextUser),
      initials: initialsFrom(nextUser),
      avatarUrl: nextUser.user_metadata?.avatar_url || "",
      pillText: rolePill(nextRole),
    };

    cacheIdentity(nextIdentity);
    setCachedIdentity(nextIdentity);
    setUser(nextUser);
    setAdminRole(nextRole);
  };

  useEffect(() => {
    let mounted = true;

    (async () => {
      const { data } = await traceAsync("sidebar.admin.getUser", () => supabase.auth.getUser(), {});
      if (mounted) await loadAdminIdentity(data?.user || null);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        loadAdminIdentity(null);
        return;
      }

      if (session?.user) {
        loadAdminIdentity(session.user);
      }
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  const navItems = useMemo(
    () => [
      { label: "Dashboard", path: "/admin", icon: LayoutDashboard },
      { label: "Projects", path: "/admin/projects", icon: PlaneTakeoff },
      { label: "Profile", path: "/admin/profile", icon: UserRound },
    ],
    []
  );

  const handleLogout = async () => {
    try {
      await withTimeout(supabase.auth.signOut(), 6000, "Sign out timed out");
    } catch (error) {
      console.warn("Admin sign out did not finish cleanly:", error?.message || error);
    } finally {
      clearCachedUserAccess();
      cacheIdentity(null);
      navigate("/login", { replace: true });
    }
  };

  const avatarInitials = user ? initialsFrom(user) : cachedIdentity?.initials || "";
  const displayName = formatDisplayName(user ? displayNameFrom(user) : cachedIdentity?.displayName || "");
  const avatarUrl = user?.user_metadata?.avatar_url || cachedIdentity?.avatarUrl || "";
  const pillText = adminRole ? rolePill(adminRole) : cachedIdentity?.pillText || "";

  const ProfileBlock = () => (
    <div className="mb-4">
      <div className="flex flex-col items-center text-center">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={`${displayName} avatar`}
            className="h-20 w-20 rounded-full object-cover ring-2 ring-white/15"
          />
        ) : (
          <div
            aria-label="Admin avatar"
            className="flex h-20 w-20 items-center justify-center rounded-full bg-white/15 text-lg font-bold text-white ring-2 ring-white/10"
          >
            {avatarInitials}
          </div>
        )}

        <div className="mt-3">
          <p className="max-w-[12rem] truncate text-base font-semibold leading-tight md:text-lg">
            {displayName || "\u00a0"}
          </p>
        </div>

        <div className={`mt-2 flex justify-center ${pillText ? "" : "invisible"}`}>
          <span className="inline-flex items-center rounded-md bg-white px-2.5 py-0.5 text-[12px] font-semibold tracking-wide text-[#1A1A1A] shadow-sm ring-1 ring-black/5">
            {pillText}
          </span>
        </div>
      </div>
    </div>
  );

  const NavLinks = ({ onClick }) => (
    <nav className="space-y-2">
      {navItems.map(({ label, path, icon: Icon }) => {
        const active = pathname === path || (path !== "/admin" && pathname.startsWith(path));
        return (
          <Link
            key={label}
            to={path}
            onClick={onClick}
            className={`group flex items-center gap-3 rounded-lg px-3 py-2 transition ${
              active
                ? "border-l-4 border-white bg-white/10 text-white"
                : "text-white/90 hover:bg-white/5 hover:text-white"
            }`}
            aria-current={active ? "page" : undefined}
          >
            <Icon className="h-5 w-5 shrink-0" />
            <span className="truncate">{label}</span>
          </Link>
        );
      })}
    </nav>
  );

  const Footer = ({ onClick }) => (
    <div className="space-y-3 border-t border-white/20 pt-6">
      <button
        onClick={async () => {
          await handleLogout();
          onClick?.();
        }}
        className="flex items-center gap-3 text-sm text-white/80 hover:text-white"
        aria-label="Sign out"
      >
        <LogOut className="h-4 w-4" />
        Sign out
      </button>
    </div>
  );

  return (
    <>
      <aside className="hidden h-screen w-64 flex-col justify-between bg-black p-6 text-white md:fixed md:flex">
        <div>
          <Link to="/admin" className="mb-8 flex justify-center" aria-label="Go to admin dashboard">
            <img src={strobeLogo} alt="Strobe Aviation" className="w-40" />
          </Link>

          <div className="mt-6">
            <ProfileBlock />
          </div>

          <div className="mt-6">
            <NavLinks />
          </div>
        </div>

        <Footer />
      </aside>

      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed right-4 top-4 z-50 text-[#d3222a] md:hidden"
          aria-label="Open Menu"
          aria-expanded="false"
        >
          <Menu size={28} />
        </button>
      )}

      {isOpen && (
        <div className="fixed left-0 top-0 z-40 flex h-full w-64 flex-col bg-black p-6 text-white md:hidden">
          <button
            onClick={() => setIsOpen(false)}
            aria-label="Close Menu"
            aria-expanded="true"
            className="absolute right-6 top-6"
          >
            <X size={24} className="text-white" />
          </button>

          <div className="mt-10 mb-6 flex w-full justify-center">
            <Link to="/admin" onClick={() => setIsOpen(false)}>
              <img src={strobeLogo} alt="Strobe Aviation" className="w-40" />
            </Link>
          </div>

          <div className="mt-6">
            <ProfileBlock />
          </div>

          <div className="mt-6">
            <NavLinks onClick={() => setIsOpen(false)} />
          </div>

          <div className="mt-auto">
            <Footer onClick={() => setIsOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
}
